import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, VariantSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery with variant-based image filtering.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Store initial media state for variant filtering
    this.#initializeMediaVariantMapping();
  }

  #controller = new AbortController();
  #mediaVariantMap = new Map();
  #allMedia = [];

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Initialize mapping of media to their associated variant IDs
   */
  #initializeMediaVariantMapping() {
    const mediaElements = this.querySelectorAll('[data-media-id]');
    
    mediaElements.forEach((media, index) => {
      const mediaId = media.dataset.mediaId;
      const variantIds = media.dataset.variantIds ? media.dataset.variantIds.split(',') : [];
      
      this.#allMedia.push({
        element: media,
        mediaId,
        variantIds,
        index
      });
      
      this.#mediaVariantMap.set(mediaId, {
        element: media,
        variantIds,
        index
      });
    });
  }

  /**
   * Handles variant selection by filtering to show only relevant media
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // Try to get variant ID from event first
    let variantId = event?.detail?.resource?.id;
    
    // Fallback to reading from variant picker
    if (!variantId) {
      const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
      if (variantPicker) {
        variantId = this.#getSelectedVariantId(variantPicker);
      }
    }

    if (!variantId) return;

    this.#filterMediaByVariant(variantId);
  };

  /**
   * Get the currently selected variant ID from form inputs
   * @param {HTMLElement} variantPicker - The variant picker element
   * @returns {string|null} The selected variant ID
   */
  #getSelectedVariantId(variantPicker) {
    const form = variantPicker.querySelector('form');
    if (!form) return null;

    // Try multiple methods to get variant ID
    // Method 1: Look for data-variant-id on checked inputs
    const checkedInputs = form.querySelectorAll('input[type="radio"]:checked, select option:checked');
    for (const input of checkedInputs) {
      if (input.dataset.variantId) {
        return input.dataset.variantId;
      }
    }
    
    // Method 2: Look for variant ID in hidden input
    const variantIdInput = form.querySelector('[name="id"]');
    if (variantIdInput?.value) {
      return variantIdInput.value;
    }
    
    return null;
  }

  /**
   * Filter and display only media associated with the selected variant
   * @param {string} variantId - The selected variant ID
   */
  #filterMediaByVariant(variantId) {
    let visibleMedia = [];
    let hasVariantSpecificMedia = false;

    // Find media for this specific variant
    this.#allMedia.forEach(mediaData => {
      if (mediaData.variantIds.includes(variantId)) {
        hasVariantSpecificMedia = true;
        visibleMedia.push(mediaData);
        mediaData.element.style.display = '';
        mediaData.element.removeAttribute('hidden');
      } else if (mediaData.variantIds.length === 0) {
        // Keep non-variant-specific media (global product images)
        visibleMedia.push(mediaData);
      }
    });

    // Hide media not associated with this variant
    this.#allMedia.forEach(mediaData => {
      if (!visibleMedia.includes(mediaData)) {
        mediaData.element.style.display = 'none';
        mediaData.element.setAttribute('hidden', '');
      }
    });

    // Update slideshow if it exists
    if (this.refs.slideshow && visibleMedia.length > 0) {
      // Select the first visible media
      this.refs.slideshow.select(0, undefined, { animate: true });
    }

    // Trigger slideshow update
    this.refs.slideshow?.update?.();
  }

  /**
   * Handles a variant update event.
   * Prefers server-rendered HTML (so thumbnails + layout stay in sync).
   * Falls back to client-side filtering only when no HTML is provided.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const variant = event.detail.resource;

    // If we have server-rendered HTML, always prefer replacing the gallery.
    // This guarantees that the main media, thumbnails, and layout all update
    // together based on the current variant (including metafield galleries).
    if (source) {
      const newMediaGallery = source.querySelector('media-gallery');
      if (!newMediaGallery) return;

      this.replaceWith(newMediaGallery);
      return;
    }

    // Fallback: if no HTML was provided (e.g. custom AJAX flows), try
    // client-side filtering using the media ↔ variant mapping.
    if (variant?.id && this.#allMedia.some(media => media.variantIds.length > 0)) {
      this.#filterMediaByVariant(variant.id.toString());
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
